import { z } from 'zod';
import { SUPPORTED_LANGUAGES } from '../constants/languages.ts';
import { PROPERTY_TYPES } from '../constants/index.ts';
import {
  PHOTO_INTAKE_FILTERS,
  PHOTO_QUOTE_MESSAGE_MAX,
  PHOTO_QUOTE_PRICE_KINDS,
  PHOTO_QUOTE_RANGE_WIDTH_MAX,
  PHOTO_QUOTE_RANGE_WIDTH_MIN,
  PHOTO_QUOTE_RISK_PRESETS,
  PHOTO_QUOTE_SLA_MINUTES_MAX,
  PHOTO_QUOTE_SLA_MINUTES_MIN,
  PHOTO_QUOTE_TERMS_MAX,
  roomFindingValueSchema,
} from '../photo-quotes.ts';
import { paginationSchema } from './common.ts';
import { emailFormat } from './formats.ts';

/** The property kinds the upload page offers: homes only (commercial work is quoted the usual way). */
export const PHOTO_QUOTE_PROPERTY_TYPES = [
  'house',
  'apartment',
  'condo',
  'townhouse',
  'vacation_rental',
  'other',
] as const satisfies readonly (typeof PROPERTY_TYPES)[number][];

/** Settings > Photo quotes. Every field optional: a PATCH names what it changes. */
export const photoQuoteSettingsPatchSchema = z
  .object({
    enabled: z.boolean().optional(),
    riskPreset: z.enum(PHOTO_QUOTE_RISK_PRESETS).optional(),
    rangeWidthPct: z.number().int().min(PHOTO_QUOTE_RANGE_WIDTH_MIN).max(PHOTO_QUOTE_RANGE_WIDTH_MAX).optional(),
    requireKitchen: z.boolean().optional(),
    requireEachBathroom: z.boolean().optional(),
    requireEachBedroom: z.boolean().optional(),
    /** Blank goes back to the built-in wording. */
    terms: z
      .string()
      .trim()
      .max(PHOTO_QUOTE_TERMS_MAX, `Must be ${PHOTO_QUOTE_TERMS_MAX} characters or fewer`)
      .transform((value) => (value.length === 0 ? null : value))
      .nullable()
      .optional(),
    slaMinutes: z.number().int().min(PHOTO_QUOTE_SLA_MINUTES_MIN).max(PHOTO_QUOTE_SLA_MINUTES_MAX).optional(),
  })
  .strict();
export type PhotoQuoteSettingsPatch = z.infer<typeof photoQuoteSettingsPatchSchema>;

/**
 * Step one of the upload page: who is asking. The email is required because
 * it is where the price goes; a phone number is for the office to call.
 */
export const createPhotoIntakeSchema = z.object({
  // A send makes a customer from it, and splits it into a first and a last
  // name of 153 characters each: no longer than that, it fits either way.
  name: z.string().trim().min(1, 'Enter your name').max(153),
  email: z
    .email(emailFormat('Enter a valid email address'))
    .max(320)
    .transform((value) => value.toLowerCase()),
  phone: z
    .string()
    .trim()
    .max(40)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional(),
  // The property the send makes holds 255 characters of address.
  address: z.string().trim().min(1, 'Enter the address').max(255),
  propertyType: z.enum(PHOTO_QUOTE_PROPERTY_TYPES).nullable().optional(),
  bedrooms: z.number().int().min(0).max(30).nullable().optional(),
  bathrooms: z.number().int().min(0).max(30).nullable().optional(),
  note: z
    .string()
    .trim()
    .max(2_000)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional(),
  language: z.enum(SUPPORTED_LANGUAGES).default('en'),
  /** The unticked SMS box. Absent or false does not block the upload. */
  smsOptIn: z.boolean().optional(),
  turnstileToken: z.string().max(4_096).optional(),
});
export type CreatePhotoIntakeInput = z.infer<typeof createPhotoIntakeSchema>;

/**
 * Sending an upload. `aiNotice` is whether the page told the customer an AI
 * service reads their photos: without it, they are not read.
 */
export const submitPhotoIntakeSchema = z.object({
  aiNotice: z.boolean().default(false),
  /** What the page that named the AI service handed its script; the flag alone proves nothing. */
  aiNoticeProof: z.string().max(128).nullish(),
});

export const photoIntakeListQuerySchema = paginationSchema.pick({ page: true, pageSize: true }).extend({
  filter: z.enum(PHOTO_INTAKE_FILTERS).default('open'),
});

/**
 * An owner's correction to one finding. `version` is the findings version the
 * screen was showing, so two people editing at once cannot price over each
 * other unseen. The value is checked against the finding's kind server-side.
 */
export const editPhotoFindingSchema = z.object({
  version: z.number().int().min(0),
  value: z.unknown(),
});

/** A room the photos missed and the owner knows is there. */
export const addPhotoRoomSchema = z.object({
  version: z.number().int().min(0),
  value: roomFindingValueSchema,
});

export const sendPhotoQuoteSchema = z.object({
  confirmHourlyPricing: z.boolean().optional(),
  version: z.number().int().min(0),
  kind: z.enum(PHOTO_QUOTE_PRICE_KINDS),
  /** Overrides the address the customer gave, as the quote send does. */
  recipientEmail: z.email(emailFormat('Enter a valid email address')).max(320).optional(),
  message: z.string().trim().max(PHOTO_QUOTE_MESSAGE_MAX).optional(),
});
export type SendPhotoQuoteInput = z.infer<typeof sendPhotoQuoteSchema>;

export const PHOTO_REQUEST_NEEDS = ['kitchen', 'bathrooms', 'bedrooms', 'living_areas', 'other'] as const;
export type PhotoRequestNeed = (typeof PHOTO_REQUEST_NEEDS)[number];

export const askMorePhotosSchema = z.object({
  needed: z.array(z.enum(PHOTO_REQUEST_NEEDS)).max(PHOTO_REQUEST_NEEDS.length).default([]),
  message: z.string().trim().max(PHOTO_QUOTE_MESSAGE_MAX).optional(),
});

/** Closes the photo quote, so like a send it names the version on screen. */
export const photoWalkthroughSchema = z.object({
  version: z.number().int().min(0),
  message: z.string().trim().max(PHOTO_QUOTE_MESSAGE_MAX).optional(),
});

/** Reading the photos again replaces the findings on screen, so it names their version. */
export const photoReanalyzeSchema = z.object({
  version: z.number().int().min(0),
});
